/* eslint-disable max-lines */

import {
    EffectArgs,
    EffectProcessArgs,
    EffectRetval,
    NotificationKey,
    NotificationQueueEntry,
    NotificationType,
} from './types'
import {
    ninchatEventActions,
    ninchatEventExtraActions,
} from 'api/slice'
import {NinchatEventCause} from '../../api/types/eventCauses'
import {NinchatInfoMessageTypes} from '../../api/types/messages'
import {notifyQueueAudienceRequest} from 'middlewares/ninchat/queuePickupDelayActions'

export const testIdle = (isIdle: boolean, ignoreIdle: boolean) => {
    if (!isIdle && !ignoreIdle) {
        throw new Error('idle')
    }
}

export const privateMessages = {
    effect: (args: EffectArgs): EffectRetval => {
        const {
            action: {
                meta: {
                    fromSelf,
                    isInfoMessage,
                },
                payload,
            },
            listenerApi: {getState},
        } = args

        const {event} = payload

        const {route} = history.state

        const isActiveDialogue = event.user_id === route?.params?.dialogue_id
        const isDialogue = !!event.user_id
        const isIdle = !!getState().ninchat.user_attrs.idle

        if (!isDialogue) {
            throw new Error('base')
        }

        if (fromSelf) {
            throw new Error('self')
        }

        if (isActiveDialogue && !isIdle) {
            throw new Error('active')
        }

        if (isInfoMessage) {
            throw new Error('type')
        }

        return [NotificationType.MessageReceivedDialogue, payload]
    },
    type: ninchatEventExtraActions.message_received.fulfilled.type,
}

export const channelHighlights = {
    effect: (args: EffectArgs): EffectRetval => {
        const {
            action: {
                meta: {
                    fromSelf,
                    isHighlighting,
                    isInfoMessage,
                },
                payload,
            },
            listenerApi: {getState},
        } = args

        const {event} = payload

        const {route} = history.state

        const isActiveChannel = event.channel_id === route?.params?.channel_id
        const isAudience = !!getState().ninchat.user_channels?.[event.channel_id]?.channel_attrs.audience_id
        const isChannel = !!event.channel_id && !isAudience
        const isIdle = !!getState().ninchat.user_attrs.idle

        if (!isChannel || !isHighlighting) {
            throw new Error('base')
        }

        if (fromSelf) {
            throw new Error('self')
        }

        if (isActiveChannel && !isIdle) {
            throw new Error('active')
        }

        if (isInfoMessage) {
            throw new Error('type')
        }

        return [NotificationType.MessageReceivedChannel, payload]
    },
    type: ninchatEventExtraActions.message_received.fulfilled.type,
}

export const channelJoins = {
    effect: (args: EffectArgs): EffectRetval => {
        const {
            action: {payload},
            listenerApi: {getState},
        } = args

        const {
            event,
            payload: messagePayload,
        } = payload

        const {route} = history.state

        const hideJoinAndPartMessages = !!getState().ninchat.user_settings.hide_join_and_part_messages
        const isActiveChannel = event.channel_id === route?.params?.channel_id
        const isAudience = !!getState().ninchat.user_channels?.[event.channel_id]?.channel_attrs.audience_id
        const isChannel = !!event.channel_id && !isAudience
        const isIdle = !!getState().ninchat.user_attrs.idle
        const isJoinMessage = event.message_type === NinchatInfoMessageTypes.NinchatInfoJoinMessage
        const self = getState().ninchat.user_id
        const isSelf = messagePayload.user_id === self

        if (!isChannel || !isJoinMessage) {
            throw new Error('base')
        }

        if (isSelf) {
            throw new Error('self')
        }

        if (isActiveChannel && !isIdle) {
            throw new Error('active')
        }

        if (hideJoinAndPartMessages) {
            throw new Error('hide')
        }

        return [NotificationType.MessageReceivedChannelJoin, payload]
    },
    type: ninchatEventExtraActions.message_received.fulfilled.type,
}

export const allChannelMessages = {
    effect: (args: EffectArgs): EffectRetval => {
        const {
            action: {
                meta: {
                    fromSelf,
                    isInfoMessage,
                },
                payload,
            },
            listenerApi: {getState},
        } = args

        const {event} = payload

        const {route} = history.state

        const isActiveChannel = event.channel_id === route?.params?.channel_id
        const isAudience = !!getState().ninchat.user_channels?.[event.channel_id]?.channel_attrs.audience_id
        const isChannel = !!event.channel_id && !isAudience
        const isIdle = !!getState().ninchat.user_attrs.idle

        if (!isChannel) {
            throw new Error('base')
        }

        if (fromSelf) {
            throw new Error('self')
        }

        if (isActiveChannel && !isIdle) {
            throw new Error('active')
        }

        if (isInfoMessage) {
            throw new Error('type')
        }

        return [NotificationType.MessageReceivedChannel, payload]
    },
    type: ninchatEventExtraActions.message_received.fulfilled.type,
}

export const newVisitorInAudienceQueue = {
    effect: (args: EffectArgs): EffectRetval => {
        const {
            action: {payload},
            listenerApi: {getState},
        } = args

        const {
            event_cause,
            queue_id,
        } = payload

        const isAudienceRequest = event_cause === NinchatEventCause.NinchatAudienceRequestEventCause
        const isMember = queue_id in (getState().ninchat.user_queues ?? {})

        if (!isAudienceRequest) {
            throw new Error('base')
        }

        if (!isMember) {
            throw new Error('member')
        }

        return [NotificationType.QueueUpdatedAudienceRequest, payload]
    },
    type: notifyQueueAudienceRequest.type,
}

export const visitorPickedFromAudienceQueue = {
    effect: (args: EffectArgs): EffectRetval => {
        const {
            action: {payload},
            listenerApi: {getState},
        } = args

        const {
            event_cause,
            queue_id,
        } = payload

        const isAudienceAcceptPeer = event_cause === NinchatEventCause.NinchatAudienceAcceptPeerEventCause
        const isMember = queue_id in (getState().ninchat.user_queues ?? {})

        if (!isAudienceAcceptPeer) {
            throw new Error('base')
        }

        if (!isMember) {
            throw new Error('member')
        }

        return [NotificationType.QueueUpdatedAudienceAcceptPeer, payload]
    },
    type: ninchatEventActions.queue_updated.type,
}

export const audienceMessages = {
    effect: (args: EffectArgs): EffectRetval => {
        const {
            action: {
                meta: {fromSelf},
                payload,
            },
            listenerApi: {getState},
        } = args

        const {event} = payload

        const {route} = history.state

        const isActiveChannel = event.channel_id === route?.params?.channel_id
        const isAudience = !!getState().ninchat.user_channels?.[event.channel_id]?.channel_attrs.audience_id
        const isIdle = !!getState().ninchat.user_attrs.idle

        if (!isAudience) {
            throw new Error('base')
        }

        if (fromSelf) {
            throw new Error('self')
        }

        if (isActiveChannel && !isIdle) {
            throw new Error('active')
        }

        return [NotificationType.MessageReceivedChannelAudience, payload]
    },
    type: ninchatEventExtraActions.message_received.fulfilled.type,
}

export const registeredContacts = {
    effect: (args: EffectArgs): EffectRetval => {
        const {action: {payload}} = args

        const {queue_activity} = payload

        const isCorrectType = queue_activity.length === 1 && queue_activity[0].event === 'audience_complete'

        if (!isCorrectType) {
            throw new Error('type')
        }

        return [NotificationType.QueueActivity, payload]
    },
    type: ninchatEventActions.queue_activity.type,
}

export const effectPick = (key: NotificationKey) => {
    const _key = key.replace(/_audio$/u, '') as NotificationKey

    switch (_key) {
        case 'private': return privateMessages
        case 'highlight': return channelHighlights
        case 'channel_join': return channelJoins
        case 'channel': return allChannelMessages
        case 'audience_queue': return newVisitorInAudienceQueue
        case 'audience_queue_picked': return visitorPickedFromAudienceQueue
        case 'audience': return audienceMessages
        case 'audience_register': return registeredContacts
        default: return {} as {type: string, effect: undefined}
    }
}

export const effectProcess = (args: EffectProcessArgs) => {
    const {
        flushNotificationQueue,
        ignoreIdle,
        listener,
        notificationQueue,
        ...effectArgs
    } = args

    if (listener) {
        const cache = new Map<NotificationType, NotificationQueueEntry>()

        for (const [key, effect] of listener.effects.entries()) {
            try {
                const isAudio = key.endsWith('_audio')
                const isIdle = !!effectArgs.listenerApi.getState().ninchat.user_attrs.idle

                testIdle(isIdle, !!ignoreIdle)

                const [type, payload] = effect(effectArgs)

                if (!cache.has(type)) {
                    cache.set(type, {
                        audio: false,
                        desktop: false,
                        payload,
                        type,
                    })
                }

                const entry = cache.get(type) as NotificationQueueEntry

                if (isAudio) {
                    entry.audio = true
                } else {
                    entry.desktop = true
                }
            } catch {} // eslint-disable-line no-empty
        }

        if (cache.size) {
            notificationQueue.push(...cache.values())
            flushNotificationQueue()
        }
    }
}
